import { sdsBaseScales, sdsChartTextColor, sdsColors, sdsGrayscale } from '../../../theme/sds-tokens';

/**
 * Sequencia de cores dos graficos do dashboard: dez cores, ordem fixa.
 *
 * E a paleta que o produto usava na v1.2.10, retomada a pedido do time em
 * 2026-09-22 depois de comparar as duas em tela. As seis primeiras sao as que
 * aparecem nos donuts da aba NR1 daquela versao; as quatro seguintes completam
 * a sequencia, na mesma ordem em que estavam no literal antigo de `Home.tsx`.
 * A cor depende da POSICAO da serie, nao do valor. Passando de dez series a
 * sequencia repete, que e o que a regra 5 do SDS permite.
 *
 * **Estas dez cores nao sao tokens do SDS.** E um desvio deliberado e amplo de
 * `$chart-colors`, decidido pelo time, e vale so para os graficos do
 * dashboard: em qualquer outro lugar a paleta fechada do SDS continua valendo.
 * Nao replique estes hex fora daqui nem os trate como precedente.
 *
 * O que se perde, medido e aceito: cinco delas ficam abaixo dos 3:1 que a WCAG
 * 1.4.11 pede para elemento grafico sobre o card branco — #FED469 (1,42:1),
 * #2DD681 (1,90:1), #FEA190 (1,96:1), #ABB7C5 (2,04:1) e #FF7043 (2,74:1).
 * Detalhe e contexto da decisao em `.llm-root/contexto-migracao.md`.
 *
 * A lista e a mesma nos dois modos, como era na v1.2.10. Sobre o fundo escuro
 * do tema so #673AB7 fica abaixo de 3:1 (2,56:1); as outras nove vao de 4,3:1
 * a 13,2:1.
 */
const chartColors = [
  '#4A81F5', // azul
  '#936DF5', // roxo
  '#2DD681', // verde
  '#FED469', // amarelo
  '#FEA190', // salmao
  '#E91E63', // rosa
  '#26A69A', // teal
  '#673AB7', // roxo escuro
  '#FF7043', // laranja
  '#ABB7C5', // cinza azulado
];

export const chartColorsFor = (isDark: boolean): string[] => chartColors;

/**
 * Barra ou fatia sem valor semantico ("Nao informado", "outros"). No SDS e o
 * `$other-color` (#C1C1CC, grayscale-30); no modo escuro o equivalente na
 * escala de cinzas e o grayscale-70.
 */
export const chartNeutralColor = (isDark: boolean): string => (isDark ? sdsGrayscale[70] : sdsGrayscale[30]);

/**
 * Destaque de uma barra unica sobre as demais (p.ex. a maior faixa etaria).
 * Clairvoyant, que e a base do SDS mais proxima do roxo usado antes da
 * migracao: 8,6:1 contra a barra neutra no modo claro. No modo escuro a logica
 * inverte — a barra destacada e a mais clara (3,9:1 contra a neutra).
 */
export const chartAccentColor = (isDark: boolean): string =>
  isDark ? sdsBaseScales.clairvoyant[100] : sdsBaseScales.clairvoyant[500];

/** Texto de apoio (eixos, legendas) legivel em cada modo. */
export const chartMutedTextColor = (isDark: boolean): string =>
  /* `text.secondary` do SDS (#999999) da 2,85:1 sobre branco e reprova em texto
     de 11-12px. Desvio deliberado para grayscale-60 (5,26:1), como orienta a
     skill do design system. */
  isDark ? 'rgba(255, 255, 255, 0.7)' : sdsGrayscale[60];

const channel = (value: number) => {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};

const luminance = (hex: string) => {
  const rgb = parseInt(hex.slice(1), 16);
  return 0.2126 * channel((rgb >> 16) & 255) + 0.7152 * channel((rgb >> 8) & 255) + 0.0722 * channel(rgb & 255);
};

const contrast = (a: number, b: number) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);

const MIN_NORMAL_TEXT_CONTRAST = 4.5;

/**
 * Cor de texto sobre uma cor da sequencia (rotulo dentro da barra ou fatia).
 *
 * Vale a regra oficial do SDS (`sdsChartTextColor`) sempre que ela atinge os
 * 4,5:1 de texto normal. Nas cores em que nenhuma das duas cores de texto
 * chega la — lime-700 e sunset-orange-500, as excecoes que a propria
 * documentacao reconhece — a escolha passa a ser a de maior contraste, como
 * orienta a skill: entre token oficial e acessibilidade, acessibilidade.
 * Na pratica isso muda uma cor: sobre lime-700 o texto vai de branco (3,05:1)
 * para #333333 (4,14:1). As variacoes tonais nao tem regra publicada e caem
 * no mesmo calculo.
 */
export const readableTextOn = (background: string) => {
  const bg = luminance(background);
  const onLight = contrast(bg, luminance(sdsColors.textPrimary));
  const onDark = contrast(bg, luminance(sdsColors.textLight));

  const official = sdsChartTextColor[background.toUpperCase()];
  const officialContrast = official === sdsColors.textLight ? onDark : onLight;

  if (official && officialContrast >= MIN_NORMAL_TEXT_CONTRAST) {
    return official;
  }

  return onDark >= onLight ? sdsColors.textLight : sdsColors.textPrimary;
};

/**
 * Genero. Fica fora de `$chart-colors` de proposito: sao as cores que a secao
 * NR1 usa desde a v1.2.10, e a mesma dimensao precisa ler igual nas duas abas
 * do dashboard. Desvio deliberado da paleta do SDS, decidido em 2026-09-22 e
 * registrado em `.llm-root/contexto-migracao.md`.
 */
export const chartGenderColors: Record<string, string> = {
  Masculino: '#4A81F5',
  Feminino: '#AF66BD',
};

/**
 * Status do documento. E criticidade, nao distincao cromatica — a regra 5 do
 * SDS proibe a sequencia de graficos aqui. Sai das cores de criticidade do
 * SDS. "Pendente" nao e criticidade, e ausencia de desfecho, entao usa o
 * neutro da propria sequencia.
 */
export const chartStatusColors = (isDark: boolean): Record<string, string> => ({
  Pendente: chartNeutralColor(isDark),
  Aprovado: sdsColors.success,
  Reprovado: sdsColors.error,
  Arquivado: sdsColors.danger,
});

/**
 * Faixas do velocimetro de media de dias, na ordem baixo → medio → alto.
 * Mesma razao do status: criticidade tem as suas cinco cores proprias.
 */
export const chartSeverityColors = [sdsColors.success, sdsColors.warning, sdsColors.error];

/**
 * Titulo e subtitulo de card de grafico. Sao os mesmos hex que os graficos
 * montados dentro de `Home.tsx` ja usavam; ficam aqui para os componentes
 * proprios lerem do mesmo lugar em vez de repetir o literal.
 *
 * Nao mudam com o modo, de proposito: a tela inteira precisa ter titulo da
 * mesma cor, e os graficos de `Home.tsx` nunca leram o tema. Limitacao aceita
 * junto: no modo escuro o titulo fica em 2,67:1 sobre o fundo do tema, abaixo
 * dos 3:1 que a WCAG pede para texto grande. Vale para os quinze titulos do
 * dashboard, nao so para estes — resolver pede um token unico para todos.
 *
 * Nenhum dos dois e token do SDS. `#54586E` fica a distancia 8 do
 * `grayscale-70` e `#8D95A0` a 13 do `grayscale-50`.
 */
export const chartTitleColor = '#54586E';
export const chartSubtitleColor = '#8D95A0';

/* Fundo do card de grafico no modo escuro: levemente mais claro que o fundo
 * da pagina (`#121212`), so para o card se destacar. Valor escolhido pelo time
 * em 2026-10-05 — `#1E1E1E` ficou claro demais e `#171616` fraco. Nao e token
 * do SDS — o SDS nao define modo escuro. No modo claro o card segue sem fundo
 * proprio. */
export const chartCardSurfaceDark = '#1A1919';

/* Linha de tendencia (atestados por dia, aba NR1): o laranja da sequencia do
 * dashboard, para a cor sair da mesma paleta dos outros graficos. */
export const chartTrendColor = chartColors[8];
